import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  Switch,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Camera24Regular,
  Dismiss24Regular,
  Video24Regular,
} from '@fluentui/react-icons';
import {
  assertRecordedMp4,
  createMp4VideoRecorder,
  readVideoBlobAsDataUrl,
} from '@moko/utils/src/videoRecordingUtils';
import { useIsMobile } from './hooks/useIsMobile';
import { notify } from './Notify';
import { PhotoEditor } from './PhotoEditor';

const useStyles = makeStyles({
  surface: {
    width: 'min(100vw - 32px, 440px)',
    maxWidth: '100%',
  },
  surfaceMobile: {
    width: 'calc(100vw - 24px)',
    maxWidth: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
  },
  previewWrap: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackgroundInverted,
  },
  preview: {
    display: 'block',
    width: '100%',
    aspectRatio: '4 / 3',
    objectFit: 'cover',
    backgroundColor: '#000',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  modeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    '> *': {
      flexGrow: 0,
      flexShrink: 0,
      width: 'auto',
      minWidth: 'unset',
      whiteSpace: 'nowrap',
    },
  },
});

export interface ImageTakerProps {
  open: boolean;
  mode?: 'photo' | 'video';
  allowVideo?: boolean;
  onCapture: (
    dataUrl: string,
    location?: { latitude: number; longitude: number }
  ) => void;
  onClose: () => void;
}

export const ImageTaker = ({
  open,
  mode = 'photo',
  allowVideo = false,
  onCapture,
  onClose,
}: ImageTakerProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const styles = useStyles();
  const [preview, setPreview] = useState<string | null>(null);
  const [capturedLocation, setCapturedLocation] = useState<
    { latitude: number; longitude: number } | undefined
  >();
  const [photoEditing, setPhotoEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraRequestRef = useRef(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [captureMode, setCaptureMode] = useState<'photo' | 'video'>(mode);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  const stopCamera = useCallback(() => {
    cameraRequestRef.current += 1;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  const startCamera = useCallback(
    async (targetMode: 'photo' | 'video') => {
      stopCamera();
      const requestId = ++cameraRequestRef.current;
      setLoading(true);

      const videoConstraints: MediaTrackConstraints | boolean =
        targetMode === 'video'
          ? {
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            }
          : true;

      try {
        let mediaStream: MediaStream;
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: videoConstraints,
            audio: targetMode === 'video',
          });
        } catch (mediaError) {
          if (
            targetMode !== 'video' ||
            requestId !== cameraRequestRef.current
          ) {
            throw mediaError;
          }
          console.warn(
            '[ImageTaker] Microphone unavailable; recording silent MP4 video:',
            mediaError
          );
          mediaStream = await navigator.mediaDevices.getUserMedia({
            video: videoConstraints,
            audio: false,
          });
        }

        if (requestId !== cameraRequestRef.current) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = mediaStream;
        setStream(mediaStream);
      } catch {
        if (requestId === cameraRequestRef.current) {
          notify.error(
            t('unableToAccessCamera', 'Unable to access the camera.')
          );
        }
      } finally {
        if (requestId === cameraRequestRef.current) {
          setLoading(false);
        }
      }
    },
    [stopCamera, t]
  );

  useEffect(() => {
    if (!open) {
      stopCamera();
      return;
    }

    const initialMode = allowVideo ? mode : 'photo';
    setCaptureMode(initialMode);
    setPreview(null);
    setCapturedLocation(undefined);
    setPhotoEditing(false);
    setRecording(false);
    void startCamera(initialMode);

    return stopCamera;
  }, [open, allowVideo, mode, startCamera, stopCamera]);

  const videoCallbackRef = useCallback(
    (node: HTMLVideoElement | null) => {
      videoRef.current = node;
      if (node && stream) {
        node.srcObject = stream;
      }
    },
    [stream]
  );

  const getLocation = () =>
    new Promise<{ latitude: number; longitude: number } | null>((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          }),
        () => resolve(null)
      );
    });

  const handleTakePhoto = async () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      notify.error(
        t('cameraNotReady', 'The camera is not ready. Please try again.')
      );
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      notify.error(t('captureFailed', 'The photo could not be captured.'));
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg');
    const location = await getLocation();
    setPreview(dataUrl);
    setCapturedLocation(location || undefined);
    stopCamera();
  };

  const handleRetake = () => {
    setPreview(null);
    setCapturedLocation(undefined);
    setPhotoEditing(false);
    void startCamera('photo');
  };

  const handleUsePhoto = () => {
    if (!preview) return;
    onCapture(preview, capturedLocation);
  };

  const handleStartRecording = () => {
    if (!stream) return;
    recordedChunksRef.current = [];

    let selection;
    try {
      selection = createMp4VideoRecorder(stream);
    } catch (recordingError) {
      notify.error(
        recordingError instanceof Error
          ? recordingError.message
          : 'This browser cannot record MP4 video.'
      );
      return;
    }

    const { recorder: mediaRecorder, requestedMimeType } = selection;
    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) recordedChunksRef.current.push(event.data);
    };

    mediaRecorder.onerror = () => {
      notify.error(t('videoRecordingFailed', 'Video recording failed.'));
      setRecording(false);
      mediaRecorderRef.current = null;
    };

    mediaRecorder.onstop = async () => {
      try {
        if (recordedChunksRef.current.length === 0) {
          throw new Error(
            'The browser did not produce any video data. Please try again.'
          );
        }
        const mimeType = assertRecordedMp4(
          mediaRecorder.mimeType,
          requestedMimeType,
          recordedChunksRef.current
        );
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const dataUrl = await readVideoBlobAsDataUrl(blob);
        const location = await getLocation();
        setPreview(dataUrl);
        stopCamera();
        setRecording(false);
        onCapture(dataUrl, location || undefined);
      } catch (recordingError) {
        notify.error(
          recordingError instanceof Error
            ? recordingError.message
            : 'The browser did not produce an MP4 video.'
        );
        setRecording(false);
      } finally {
        mediaRecorderRef.current = null;
      }
    };

    try {
      mediaRecorder.start();
      setRecording(true);
    } catch (recordingError) {
      mediaRecorderRef.current = null;
      notify.error(
        recordingError instanceof Error
          ? recordingError.message
          : 'Video recording could not be started.'
      );
    }
  };

  const handleStopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
  };

  const handleCancel = () => {
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    if (recorder && recorder.state !== 'inactive') {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      try {
        recorder.stop();
      } catch {
        // The stream is stopped below even if MediaRecorder already stopped.
      }
    }
    setRecording(false);
    stopCamera();
    onClose();
  };

  const handleModeChange = (nextMode: 'photo' | 'video') => {
    setCaptureMode(nextMode);
    setPreview(null);
    void startCamera(nextMode);
  };

  const title =
    captureMode === 'video'
      ? t('recordVideo', 'Record video')
      : t('takePhoto', 'Take photo');

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DialogSurface
        className={isMobile ? styles.surfaceMobile : styles.surface}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label={t('close', 'Close')}
                icon={<Dismiss24Regular />}
                onClick={handleCancel}
              />
            }
          >
            {title}
          </DialogTitle>
          <DialogContent className={styles.content}>
            {preview && captureMode === 'photo' ? (
              <PhotoEditor
                src={preview}
                onSrcChange={setPreview}
                onEditingChange={setPhotoEditing}
              />
            ) : (
              <div className={styles.previewWrap}>
                {!preview && (
                  <video
                    ref={videoCallbackRef}
                    autoPlay
                    playsInline
                    muted
                    className={styles.preview}
                  />
                )}
                {preview && (
                  <video src={preview} controls className={styles.preview} />
                )}
                {loading && (
                  <div className={styles.overlay}>
                    <Spinner label={t('loading', 'Loading...')} />
                  </div>
                )}
              </div>
            )}
            {allowVideo && !preview && (
              <div className={styles.modeRow}>
                <Text>{t('photo', 'Photo')}</Text>
                <Switch
                  checked={captureMode === 'video'}
                  disabled={recording}
                  onChange={(_, data) =>
                    handleModeChange(data.checked ? 'video' : 'photo')
                  }
                  label={t('video', 'Video')}
                />
              </div>
            )}
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button appearance="secondary" onClick={handleCancel}>
              {t('cancel', 'Cancel')}
            </Button>
            {preview && captureMode === 'photo' ? (
              <>
                <Button appearance="secondary" onClick={handleRetake}>
                  {t('retake', 'Retake')}
                </Button>
                <Button
                  appearance="primary"
                  disabled={photoEditing}
                  onClick={handleUsePhoto}
                >
                  {t('usePhoto', 'Use photo')}
                </Button>
              </>
            ) : captureMode === 'photo' ? (
              <Button
                appearance="primary"
                icon={<Camera24Regular />}
                disabled={loading || !stream}
                onClick={() => void handleTakePhoto()}
              >
                {t('capture', 'Capture')}
              </Button>
            ) : !recording ? (
              <Button
                appearance="primary"
                icon={<Video24Regular />}
                disabled={loading || !stream}
                onClick={handleStartRecording}
              >
                {t('startRecording', 'Start recording')}
              </Button>
            ) : (
              <Button
                appearance="primary"
                icon={<Video24Regular />}
                onClick={handleStopRecording}
              >
                {t('stopRecording', 'Stop recording')}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ImageTaker;
